import React, { useMemo, useState } from 'react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon, ArrowUpDownIcon } from 'lucide-react';
import { LOCATIONS, findLocation } from '../../lib/locations';
import type { Vote } from '../../lib/types';
type SortKey = 'date' | 'name';
const PAGE_SIZE = 8;
export function VotesTable({
  votes,
  onSelect
}: {
  votes: Vote[];
  onSelect: (v: Vote) => void;
}) {
  const [nameQuery, setNameQuery] = useState('');
  const [emailQuery, setEmailQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(0);
  const filtered = useMemo(() => {
    const n = nameQuery.trim().toLowerCase();
    const e = emailQuery.trim().toLowerCase();
    let rows = votes.filter(v => {
      if (n && !v.fullName.toLowerCase().includes(n)) return false;
      if (e && !v.email.toLowerCase().includes(e)) return false;
      if (cityFilter !== 'all' && v.city !== cityFilter) return false;
      return true;
    });
    rows = [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = a.fullName.localeCompare(b.fullName);else cmp = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      return sortAsc ? cmp : -cmp;
    });
    return rows;
  }, [votes, nameQuery, emailQuery, cityFilter, sortKey, sortAsc]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const rows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc(a => !a);else {
      setSortKey(key);
      setSortAsc(key === 'name');
    }
    setPage(0);
  }
  return <section className="rounded-3xl bg-white p-5 shadow-soft" data-id="element-110" data-mp="17wehp7sd1rkg">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between" data-id="element-111" data-mp="12ylf0oq9nfox">
        <div data-id="element-112" data-mp="kx9o65vyo33">
          <h2 className="font-display text-lg font-700 text-slate-800" data-id="element-113" data-mp="lgkz4116e85">
            Complete Vote Table
          </h2>
          <p className="text-sm text-slate-500" data-id="element-114" data-mp="5iyvuzylz3is">
            {filtered.length} {filtered.length === 1 ? 'vote' : 'votes'}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-id="element-115" data-mp="9jskxwnseod9">
          <SearchInput value={nameQuery} onChange={v => {
          setNameQuery(v);
          setPage(0);
        }} placeholder="Search by name" data-id="element-116" data-mp="ek6gwgc467ux" />
          <SearchInput value={emailQuery} onChange={v => {
          setEmailQuery(v);
          setPage(0);
        }} placeholder="Search by email" data-id="element-117" data-mp="3i1on6jc8n6t8" />
          <select aria-label="Filter by city" value={cityFilter} onChange={e => {
          setCityFilter(e.target.value);
          setPage(0);
        }} className="rounded-2xl border-2 border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-600 text-slate-700 focus:border-lavender-400 focus:bg-white focus:outline-none" data-id="element-118" data-mp="3rtb6l2yqcc72">
            <option value="all" data-id="element-119" data-mp="2mt4i3n18o3ww">All cities</option>
            {LOCATIONS.map(l => <option key={l.id} value={l.city} data-id="element-120" data-mp="2hrnli3psf3m4">
                {l.city}
              </option>)}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-121" data-mp="28t278krcmar5">
        <table className="w-full min-w-[640px] border-collapse text-left" data-id="element-122" data-mp="28u4umipgpv2b">
          <thead data-id="element-123" data-mp="240sl7ftctuu5">
            <tr className="border-b border-slate-100 text-sm text-slate-500" data-id="element-124" data-mp="240923vryx0ej">
              <Th onClick={() => toggleSort('name')} sortable active={sortKey === 'name'} data-id="element-125" data-mp="1z3sdz1krqm1g">
                Name
              </Th>
              <Th data-id="element-126" data-mp="1u5h4wt2asfka">Email</Th>
              <Th data-id="element-127" data-mp="1q22mxwustp47">Selected City</Th>
              <Th data-id="element-128" data-mp="1q2c6b1lc8y0x">Province</Th>
              <Th onClick={() => toggleSort('date')} sortable active={sortKey === 'date'} data-id="element-129" data-mp="1q19dgo4gglid">
                Date
              </Th>
              <Th data-id="element-130" data-mp="3l57dm113idj3">Time</Th>
            </tr>
          </thead>
          <tbody data-id="element-131" data-mp="3l5qvjhqqx1x4">
            {rows.length === 0 ? <tr data-id="element-132" data-mp="3p7c4wt42my2k">
                <td colSpan={6} className="py-10 text-center text-slate-400" data-id="element-133" data-mp="3p5r9l19xnb3n">
                  No votes match your filters.
                </td>
              </tr> : rows.map(v => {
            const loc = findLocation(v.city);
            return <tr key={v.id} tabIndex={0} onClick={() => onSelect(v)} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(v);
              }
            }} className="cursor-pointer border-b border-slate-50 text-sm transition hover:bg-lavender-50 focus:bg-lavender-50 focus:outline-none" data-id="element-134" data-mp="31gyl1pvn45j2">
                    <td className="py-3 pr-3 font-600 text-slate-800" data-id="element-135" data-mp="vni5ufiqoz3b">{v.fullName}</td>
                    <td className="py-3 pr-3 text-slate-600" data-id="element-136" data-mp="15sq4tk8mqlgi">{v.email}</td>
                    <td className="py-3 pr-3" data-id="element-137" data-mp="10tws3u162yhe">
                      <span className="inline-flex items-center gap-1.5 font-600 text-slate-700" data-id="element-138" data-mp="10r0u65pbk9sy">
                        <span data-id="element-139" data-mp="1emmq6wtyztuv">{loc?.emoji}</span>
                        {v.city}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-slate-600" data-id="element-140" data-mp="1oi6izad8mcfe">{v.province}</td>
                    <td className="py-3 pr-3 text-slate-600" data-id="element-141" data-mp="1jjlhz8q5vopw">{v.date}</td>
                    <td className="py-3 pr-3 text-slate-600" data-id="element-142" data-mp="1jjd5hhxyalf8">{v.time}</td>
                  </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between print:hidden" data-id="element-143" data-mp="1tfohe6tmp995">
        <span className="text-sm text-slate-500" data-id="element-144" data-mp="35vb5u7nqmck">
          Page {safePage + 1} of {pageCount}
        </span>
        <div className="flex gap-2" data-id="element-145" data-mp="3vayuimgmyoab">
          <PageBtn disabled={safePage === 0} onClick={() => setPage(p => Math.max(0, p - 1))} aria-label="Previous page" data-id="element-146" data-mp="1y8rns363hzeo">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-147" data-mp="239p7wlao13it" />
          </PageBtn>
          <PageBtn disabled={safePage >= pageCount - 1} onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} aria-label="Next page" data-id="element-148" data-mp="285wfsjmmgp3e">
            <ChevronRightIcon className="h-4 w-4" data-id="element-149" data-mp="2h6cc994y6vyr" />
          </PageBtn>
        </div>
      </div>
    </section>;
}
function SearchInput({
  value,
  onChange,
  placeholder
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return <div className="relative" data-id="element-150" data-mp="2vw0fwh0lq6pn">
      <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-151" data-mp="2vv8nff2awp3l" />
      <input type="search" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} className="w-full rounded-2xl border-2 border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-lavender-400 focus:bg-white focus:outline-none" data-id="element-152" data-mp="30wxzmy19f8t9" />
    </div>;
}
function Th({
  children,
  onClick,
  sortable,
  active
}: {
  children: React.ReactNode;
  onClick?: () => void;
  sortable?: boolean;
  active?: boolean;
}) {
  if (sortable) {
    return <th className="py-2 pr-3 font-600" data-id="element-153" data-mp="1cx2kpzqkvf1d">
        <button onClick={onClick} className={`inline-flex items-center gap-1 transition hover:text-lavender-600 ${active ? 'text-lavender-600' : ''}`} data-id="element-154" data-mp="17yiu3m11hf2o">
          {children}
          <ArrowUpDownIcon className="h-3.5 w-3.5" data-id="element-155" data-mp="y8zcsksz5t9x" />
        </button>
      </th>;
  }
  return <th className="py-2 pr-3 font-600" data-id="element-156" data-mp="1vo3febko0of4">{children}</th>;
}
function PageBtn({
  children,
  disabled,
  onClick,
  ...rest
}: {
  children: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button disabled={disabled} onClick={onClick} {...rest} className="flex h-9 w-9 items-center justify-center rounded-xl border-2 border-slate-200 text-slate-600 transition hover:border-lavender-300 hover:text-lavender-600 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-157" data-mp="z890m5mhfvvr">
      {children}
    </button>;
}